import { translate } from "app/language/service"
import { APP_CONTEXTS, ISortOrder } from "app/model"
import { getStore } from "app/store/store"
import { formEventModule } from "form/event"
import { refreshInventory } from "form/inventory/tools/common"
import { FORM_EVENTS, IForm } from "form/model"
import { state } from "form/state"
import { formActionCreator } from "form/store/actions"
import { getForm } from "form/tools/common"
import { messageSVC } from "message/service"
import { ISspAssociation } from "../../ssp/model"
import { sspRPO } from "../../ssp/repository"
import { getAllSspsInStore } from "../../ssp/tools/common"
import { getScreensChanges } from "./screens"

export function addAllSspToNewCampaign(tempCampaignId: number, isForecast: boolean, isDspDeal: boolean): Promise<void> {
  return new Promise<void>(resolve => {
    const allSsps = getAllSspsInStore()
    if (allSsps.length === 0) {
      return resolve()
    }
    const sspAssociations: ISspAssociation[] = []
    const toProcessPromiseCount = allSsps.length
    let proccessedPromise = 0
    const onFinish = (sspAssociation: ISspAssociation | null) => {
      if (sspAssociation) {
        sspAssociations.push(sspAssociation)
      }
      if (toProcessPromiseCount === ++proccessedPromise) {
        getStore().dispatch(
          formActionCreator.setFormSsps(
            isForecast
              ? APP_CONTEXTS.FORECAST_CREATE
              : isDspDeal
                ? APP_CONTEXTS.DSP_DEAL_CREATE
                : APP_CONTEXTS.CAMPAIGN_CREATE,
            [],
            sspAssociations
          )
        )
        resolve()
      }
    }
    for (const ssp of allSsps) {
      sspRPO
        .associateSsp(tempCampaignId, ssp.id)
        .then(sspAssociation => onFinish(sspAssociation))
        .catch(error => {
          console.error(error)
          onFinish(null)
        })
    }
  })
}

export function synchronizeNewSspSelection(newSelectedSspIds: number[]): void {
  getStore().dispatch(formActionCreator.startSynchronizingSsp(getForm().context.appContext))
  clearTimeout(state.sspTimeout)
  state.sspTimeout = setTimeout(() => setSsps(newSelectedSspIds), 800)
}

// @recursive
async function setSsps(newSelectedSspIds: number[]): Promise<void> {
  if (state.sspIsSynchronizing) {
    state.sspToApplySelection = newSelectedSspIds
    return
  } else {
    state.sspIsSynchronizing = true
  }
  const form = getForm()
  let hasError = false
  try {
    const newSspAssociations = await sspRPO.setSsps(form.tempCampaignId, newSelectedSspIds)
    getStore().dispatch(
      formActionCreator.setFormSsps(
        form.context.appContext,
        getScreensChanges({
          ...form,
          screens: {
            ...form.screens,
            sspIds: newSelectedSspIds
          }
        }),
        newSspAssociations
      )
    )
    notifySspSelectionChanged(newSelectedSspIds)
  } catch (error) {
    console.error(error)
    hasError = true
    getStore().dispatch(formActionCreator.setRelations(form.context.appContext, { sspsError: true }))
    getStore().dispatch(formActionCreator.stopSynchronizingSsp(form.context.appContext))
  }
  state.sspIsSynchronizing = false
  if (state.sspToApplySelection) {
    const toApplySspSelection = state.sspToApplySelection
    state.sspToApplySelection = null
    // @recursivity
    setSsps(toApplySspSelection)
  } else if (!hasError) {
    refreshInventory()
  }
}

export function notifySspSelectionChanged(newSelection: number[]): void {
  formEventModule.notify(FORM_EVENTS.SSP_SELECTION_CHANGED, newSelection)
}

export function noticeAndClearSspError(form: IForm): void {
  messageSVC.error(translate("ssp.error.selection"))
  getStore().dispatch(formActionCreator.setRelations(form.context.appContext, { sspsError: false }))
}

export function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number): T[] {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  })
  return stabilizedThis.map(el => el[0])
}

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

export function getComparator<T, Key extends keyof T>(order: ISortOrder, orderBy: Key): (a: T, b: T) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}
